import React from 'react';
import { Helmet } from 'react-helmet-async';
import { MapPin, Phone, Calendar, ArrowRight, CheckCircle, Clock, Car, Heart, Brain, Moon } from 'lucide-react';
import CTA from '../../components/CTA';

const AntrimAnxietyStress: React.FC = () => {
  return (
    <main>
      <Helmet>
        <title>Anxiety Treatment Antrim | Market Town Stress Management</title>
        <meta
          name="description"
          content="Professional anxiety and stress hypnotherapy serving Antrim from Belfast clinic (20 miles). Evidence-based treatment for commuter anxiety, work-life balance, family stress. CNHC registered."
        />
        <meta name="keywords" content="anxiety treatment Antrim, stress management Antrim, hypnotherapy Antrim anxiety, commuter stress M2, work-life balance Antrim, family anxiety" />
        <link rel="canonical" href="https://www.sharonclare-hypnotherapy.com/antrim-anxiety-stress/" />
        <meta property="og:title" content="Anxiety Treatment Antrim | Market Town Stress Management" />
        <meta property="og:description" content="Professional anxiety and stress hypnotherapy serving Antrim from Belfast. Evidence-based treatment for commuter anxiety, work-life balance stress." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Sharon Clare Hypnotherapy - Anxiety Treatment Antrim",
            "description": "CNHC registered hypnotherapy for anxiety and stress, serving Antrim from Belfast clinic. Specializing in commuter anxiety, work-life balance stress, family pressures, and retail worker anxiety.",
            "url": "https://www.sharonclare-hypnotherapy.com/antrim-anxiety-stress/",
            "telephone": "07932139245",
            "email": "Sharon@sharonclare-hypnotherapy.com",
            "image": "https://www.sharonclare-hypnotherapy.com/images/sharon-clare.jpg",
            "serviceType": "Anxiety and Stress Treatment",
            "areaServed": {
              "@type": "City",
              "name": "Antrim",
              "containedInPlace": {
                "@type": "AdministrativeArea",
                "name": "County Antrim"
              }
            },
            "location": {
              "@type": "Place",
              "name": "Belfast Clinic",
              "address": {
                "@type": "PostalAddress",
                "streetAddress": "Belfast Wellness Clinic, 228 Ormeau Road",
                "addressLocality": "Belfast",
                "postalCode": "BT7 2FZ",
                "addressCountry": "GB"
              }
            },
            "sameAs": [
              "https://instagram.com/sharonclarehypnotherapy",
              "https://www.facebook.com/SharonClareHypnotherapy/",
              "https://www.linkedin.com/in/sharonclare-hypnotherapy"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How far is Belfast from Antrim for anxiety treatment?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Belfast is approximately 20 miles from Antrim via the M2, taking around 30 minutes by car. This convenient distance makes regular anxiety treatment appointments highly practical. Many Antrim commuters already travel this route daily for work, making specialist anxiety care accessible without adding significant travel burden."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with commuter anxiety and stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, solution-focused hypnotherapy is highly effective for commuter-related anxiety. Many Antrim residents commute to Belfast via the M2 corridor, and the daily journey, traffic stress, and work-life balance challenges can trigger significant anxiety. Hypnotherapy helps by calming your stress response, developing resilience, improving sleep quality, and creating healthier thought patterns around work pressures and time management."
                }
              },
              {
                "@type": "Question",
                "name": "Do you offer flexible appointment times for working parents?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes. Clinic days and times at the Belfast clinic vary week to week, and early evening appointments are available to accommodate working schedules and family commitments. Current availability and online booking are on the clinic's own page: https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37. For maximum flexibility, I also offer secure online sessions via video call, allowing you to attend from home in Antrim without adding commute time to your already busy schedule. This is particularly helpful for parents juggling school runs and work commitments."
                }
              },
              {
                "@type": "Question",
                "name": "Can hypnotherapy help with parenting stress and family anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Absolutely. Parenting stress, particularly when combined with work pressures and commuting, can be overwhelming. Hypnotherapy helps by reducing anxiety, improving stress management, enhancing sleep quality, and building resilience. Many Antrim parents find that by addressing their own anxiety and stress, they become calmer, more patient, and better equipped to handle family challenges."
                }
              },
              {
                "@type": "Question",
                "name": "How many sessions will I need for anxiety?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, severity of symptoms, and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process."
                }
              },
              {
                "@type": "Question",
                "name": "Does hypnotherapy work for work-life balance stress?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, hypnotherapy is very effective for work-life balance stress. While it cannot change your external circumstances (your job demands, commute, or family responsibilities), it can profoundly change how you respond to these pressures. By reducing anxiety, improving sleep, building resilience, and helping you develop healthier boundaries and priorities, hypnotherapy helps you manage the demands of modern life more effectively."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-gray-900 mb-6">
              Anxiety and Stress Treatment for Antrim
            </h1>
            <p className="text-xl sm:text-2xl text-gray-600 mb-6 leading-relaxed">
              Professional hypnotherapy serving Antrim market town from our Belfast clinic
            </p>
            <div className="bg-white rounded-xl p-6 mb-8 inline-block">
              <div className="flex items-center justify-center space-x-3">
                <Car className="w-6 h-6 text-accent-600" />
                <p className="text-lg text-gray-700">
                  <span className="font-semibold">20 miles</span> from Antrim via M2 • <span className="font-semibold">~30 min drive</span>
                </p>
              </div>
              <p className="text-gray-600 mt-2">Online sessions also available for maximum flexibility</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/book"
                className="btn-primary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Free Consultation
              </a>
              <a
                href="tel:07932139245"
                className="btn-secondary text-lg px-8 py-4 inline-flex items-center justify-center"
              >
                <Phone className="w-5 h-5 mr-2" />
                Call 07932 139245
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Understanding Anxiety in Antrim */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-6">
              Understanding Anxiety and Stress in Antrim
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                If you're living in Antrim and struggling with anxiety or stress, you're not alone. As a market town with a population of around 24,000, Antrim combines traditional community character with modern suburban pressures. Located on the northeastern shore of Lough Neagh and well-connected via the M2 corridor to Belfast, many Antrim residents face the daily reality of commuting to work in Belfast while trying to maintain family life, manage household responsibilities, and preserve some sense of work-life balance.
              </p>
              <p>
                The M2 corridor that makes Belfast accessible for work also brings its own stressors - traffic anxiety, the pressure of daily commutes, time away from family, and the constant juggling act of trying to be everywhere at once. Add to this the typical pressures of modern life - financial worries, parenting stress, retail or service sector work pressures, and the challenge of finding time for yourself - and it's no wonder anxiety and stress levels can feel overwhelming.
              </p>
              <p>
                While I don't have a physical clinic in Antrim itself, I'm proud to serve the Antrim community from my Belfast clinic at Belfast Wellness Clinic, 228 Ormeau Road, Belfast. The journey from Antrim is straightforward - approximately 20 miles via the M2, taking around 30 minutes. Many Antrim residents already commute this route daily for work, making professional anxiety treatment accessible without adding significant travel burden. For those who prefer the convenience of online sessions or have irregular schedules, I also offer secure video call appointments from the comfort of your home.
              </p>
              <p>
                As a CNHC registered hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I understand the specific stressors that Antrim residents face. Commuter anxiety, work-life balance struggles, parenting pressures, retail worker stress, and the constant feeling of being pulled in too many directions - these are real issues that affect mental health and quality of life. Solution-focused hypnotherapy offers an evidence-based approach that addresses these challenges by calming your nervous system, building resilience, and helping you develop healthier responses to daily stressors.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Anxiety Affects You */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Anxiety and Stress Affect Antrim Residents
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Common Anxiety Symptoms</h3>
                <ul className="space-y-3">
                  {[
                    'Racing thoughts, especially about work or family',
                    'Difficulty falling or staying asleep',
                    'Physical tension: tight shoulders, headaches, chest tightness',
                    'Constant feeling of being overwhelmed',
                    'Irritability with family despite not wanting to be',
                    'Difficulty switching off after work',
                    'Worry about managing everything on your plate',
                    'Feeling guilty about not being "enough" at work or home'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100">
                <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">Antrim-Specific Stressors</h3>
                <ul className="space-y-3">
                  {[
                    'M2 commuter stress and traffic anxiety',
                    'Work-life balance challenges with Belfast commute',
                    'Parenting pressures and school run stress',
                    'Financial anxiety from household costs',
                    'Time poverty - never enough hours in the day',
                    'Retail or service sector work pressures',
                    'Guilt about limited family time due to commute',
                    'Feeling disconnected from local community'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-accent-600 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-white rounded-2xl p-8 mt-6">
              <p className="text-gray-700 text-lg">
                If anxiety is affecting your sleep, relationships, work performance, or quality of life, you don't have to struggle alone. Solution-focused hypnotherapy offers an evidence-based approach that addresses both the symptoms and the underlying causes of anxiety, helping you feel calm, balanced, and in control.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How Hypnotherapy Helps */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              How Hypnotherapy Helps with Anxiety and Stress
            </h2>
            <p className="text-gray-700 leading-relaxed mb-8 text-center max-w-3xl mx-auto">
              Solution-focused hypnotherapy gently shifts focus away from overwhelm and toward practical, positive change. Hypnosis calms the overactive fight-or-flight response so anxiety reduces, sleep improves, and you regain a sense of balance and control.
            </p>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {[
                {
                  icon: <Heart className="w-8 h-8 text-accent-600" />,
                  title: 'Calm the Nervous System',
                  text: 'Soothe the overactive stress response and reduce physical anxiety symptoms. Your body learns to relax even when facing daily pressures and time demands.'
                },
                {
                  icon: <Brain className="w-8 h-8 text-accent-600" />,
                  title: 'Build Resilience',
                  text: 'Develop better stress management and emotional resilience. Replace anxious overthinking with calmer, more balanced responses to work and family demands.'
                },
                {
                  icon: <Moon className="w-8 h-8 text-accent-600" />,
                  title: 'Restore Restful Sleep',
                  text: 'Stop the racing mind that keeps you awake at night. Learn to switch off and wake feeling refreshed and better equipped to handle daily challenges.'
                }
              ].map(({ icon, title, text }) => (
                <div key={title} className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8">
                  <div className="flex flex-col items-center text-center">
                    <div className="mb-4">{icon}</div>
                    <h3 className="font-heading font-semibold text-gray-900 mb-3 text-xl">{title}</h3>
                    <p className="text-gray-700">{text}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="bg-gradient-to-br from-primary-50 to-accent-50 rounded-2xl p-8">
              <h3 className="font-heading font-semibold text-gray-900 mb-4 text-xl">What Hypnotherapy Can Help With</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <ul className="space-y-2">
                  {[
                    'Generalized anxiety and chronic worry',
                    'Commuter stress and travel anxiety',
                    'Work-life balance stress and overwhelm',
                    'Parenting anxiety and family pressures'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
                <ul className="space-y-2">
                  {[
                    'Sleep problems and insomnia',
                    'Panic attacks and acute anxiety',
                    'Financial anxiety and money worries',
                    'Retail or service sector work stress'
                  ].map((item) => (
                    <li key={item} className="flex items-start space-x-3">
                      <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Sharon */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Why Choose Sharon Clare Hypnotherapy for Antrim?
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  CNHC Registered Professional Care
                </h3>
                <p className="text-gray-700">
                  As a CNHC registered clinical hypnotherapist with 15 years of nursing experience at Belfast City Hospital, I bring professional healthcare expertise to every anxiety treatment session. You can trust that you're receiving evidence-based treatment from a qualified professional who understands medical contexts and maintains strict ethical standards.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Understanding Commuter Pressures
                </h3>
                <p className="text-gray-700">
                  I understand the unique stress that comes from commuting to Belfast for work - the time away from family, traffic anxiety, work-life balance challenges, and the constant feeling of being pulled in too many directions. Having worked with many commuters, I'm sensitive to these real-world pressures and tailor treatment to address your specific circumstances.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Convenient from Antrim
                </h3>
                <p className="text-gray-700">
                  At 20 miles via the M2 (around 30 minutes drive), my Belfast clinic is accessible for regular anxiety treatment appointments from Antrim. Many residents already travel this route for work, making specialist care practical without significant additional travel. The manageable journey means you can attend sessions without it consuming your entire day.
                </p>
              </div>

              <div className="bg-white rounded-xl p-8">
                <CheckCircle className="w-10 h-10 text-accent-600 mb-4" />
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Flexible Online Sessions Available
                </h3>
                <p className="text-gray-700">
                  For maximum flexibility, I offer secure online sessions via video call. Online sessions provide the same professional anxiety treatment from home - ideal for busy parents juggling school runs and work, shift workers with irregular hours, or anyone who prefers the convenience of online therapy without adding commute time to an already busy schedule.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Getting to Belfast from Antrim */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-8 text-center">
              Getting to Belfast from Antrim
            </h2>
            <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-2xl p-8 mb-8">
              <div className="flex items-start space-x-4 mb-6">
                <Car className="w-8 h-8 text-accent-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 text-lg">By Car (Recommended)</h3>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Route:</span> Take the M2 motorway southbound into Belfast, then head south to the Ormeau Road (A24)
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Distance:</span> Around 20 miles
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Time:</span> Roughly half an hour in typical traffic
                  </p>
                  <p className="text-gray-700 mb-2">
                    <span className="font-semibold">Destination:</span> Belfast Wellness Clinic, 228 Ormeau Road, Belfast BT7 2FZ
                  </p>
                  <p className="text-gray-700">
                    <span className="font-semibold">Public transport:</span> Metro bus services run along the Ormeau Road corridor, and Botanic and City Hospital are the nearest train stations
                  </p>
                </div>
              </div>
              <div className="bg-white rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">Familiar Route for Commuters</h3>
                <p className="text-gray-700">
                  Travelling in on the M2, many Antrim residents already travel this route daily for work. This means professional anxiety treatment is accessible without learning a new route or adding significant travel burden. The journey is a straightforward run of roughly half an hour, making it practical to attend sessions without consuming large amounts of your day.
                </p>
              </div>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-accent-200">
              <h3 className="font-semibold text-gray-900 mb-4 text-lg">Online Sessions Alternative</h3>
              <p className="text-gray-700">
                Prefer attending from home? I offer secure online hypnotherapy sessions via video call. Online sessions provide the same professional anxiety treatment with the convenience of attending from your home in Antrim. This option is ideal for busy parents, shift workers with irregular hours, or anyone who prefers the flexibility of online therapy without adding commute time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 sm:py-20 bg-gradient-to-br from-primary-50 to-accent-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              What Antrim Clients Say
            </h2>
            <div className="space-y-6">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "I was completely overwhelmed - commuting to Belfast every day, trying to manage work pressures, rushing back for school pickup, never having any time for myself or my partner. The anxiety was constant and I wasn't sleeping. Sharon's hypnotherapy helped me manage the stress and find some balance. I'm still busy, but I handle it differently now. The anxiety doesn't control me anymore and I'm sleeping properly for the first time in years."
                </p>
                <p className="font-semibold text-gray-900">J.M., Antrim - Work-Life Balance Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "The M2 commute to work was making me anxious every single day - the traffic, the time pressure, worrying about getting home on time. It sounds silly but it was affecting everything. After hypnotherapy with Sharon, I handle the commute completely differently. I use online sessions because of my irregular hours and it's been brilliant. The driving anxiety has gone and I'm much calmer generally."
                </p>
                <p className="font-semibold text-gray-900">R.K., Antrim - Commuter Anxiety</p>
              </div>

              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <p className="text-lg text-gray-700 italic mb-4">
                  "Working in retail while trying to manage family life, I was constantly stressed and snapping at my kids even though I didn't want to. Sharon helped me understand the anxiety and gave me tools to manage it. I'm still in the same job with the same responsibilities, but I'm calmer, more patient, and I actually enjoy spending time with my family now instead of feeling irritable all the time."
                </p>
                <p className="font-semibold text-gray-900">L.T., Antrim - Parenting Stress and Retail Work Anxiety</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-12 text-center">
              Frequently Asked Questions - Antrim Anxiety Treatment
            </h2>
            <div className="space-y-6">
              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How far is Belfast from Antrim for anxiety treatment?
                </h3>
                <p className="text-gray-700">
                  Belfast is approximately 20 miles from Antrim via the M2, taking around 30 minutes by car. This convenient distance makes regular anxiety treatment appointments highly practical. Many Antrim commuters already travel this route daily for work, making specialist anxiety care accessible without adding significant travel burden.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with commuter anxiety and stress?
                </h3>
                <p className="text-gray-700">
                  Yes, solution-focused hypnotherapy is highly effective for commuter-related anxiety. Many Antrim residents commute to Belfast via the M2 corridor, and the daily journey, traffic stress, and work-life balance challenges can trigger significant anxiety. Hypnotherapy helps by calming your stress response, developing resilience, improving sleep quality, and creating healthier thought patterns around work pressures and time management.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Do you offer flexible appointment times for working parents?
                </h3>
                <p className="text-gray-700">
                  Yes. Clinic days and times at the Belfast clinic vary week to week, and early evening appointments are available to accommodate working schedules and family commitments. Current availability and online booking are on the{' '}
                  <a
                    href="https://belfastchiropracticclinic.janeapp.co.uk/#/staff_member/37"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-600 hover:text-primary-700 font-medium underline"
                  >
                    clinic booking page
                  </a>
                  . For maximum flexibility, I also offer secure online sessions via video call, allowing you to attend from home in Antrim without adding commute time to your already busy schedule. This is particularly helpful for parents juggling school runs and work commitments.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Can hypnotherapy help with parenting stress and family anxiety?
                </h3>
                <p className="text-gray-700">
                  Absolutely. Parenting stress, particularly when combined with work pressures and commuting, can be overwhelming. Hypnotherapy helps by reducing anxiety, improving stress management, enhancing sleep quality, and building resilience. Many Antrim parents find that by addressing their own anxiety and stress, they become calmer, more patient, and better equipped to handle family challenges.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  How many sessions will I need for anxiety?
                </h3>
                <p className="text-gray-700">
                  Most clients see significant improvements within 4-6 sessions for anxiety and stress. The number varies based on your individual circumstances, severity of symptoms, and goals. We'll discuss your specific needs during the free initial consultation and create a tailored treatment plan. I offer a discounted 4-session package which provides better value and ensures commitment to the process.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">
                  Does hypnotherapy work for work-life balance stress?
                </h3>
                <p className="text-gray-700">
                  Yes, hypnotherapy is very effective for work-life balance stress. While it cannot change your external circumstances (your job demands, commute, or family responsibilities), it can profoundly change how you respond to these pressures. By reducing anxiety, improving sleep, building resilience, and helping you develop healthier boundaries and priorities, hypnotherapy helps you manage the demands of modern life more effectively.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <CTA
        title="Start Your Journey to Balance and Calm"
        subtitle="Take the first step toward less anxiety, better sleep, and more balance. Book a free consultation today and discover how hypnotherapy can help you manage stress and regain control."
        primaryButtonText="Book Free Consultation"
        primaryButtonHref="/book"
        secondaryButtonText="Call Now"
        secondaryButtonHref="tel:07932139245"
        variant="accent"
      />
    </main>
  );
};

export default AntrimAnxietyStress;
